import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, FormControlLabel, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import { carbonReferenceChoices } from "./corporateCarbon/referenceData";
import { CarbonAdminPage, CarbonDataTable, CarbonFilterBar, CarbonSection, CarbonSelectInput, CarbonTableActions, CarbonTextInput } from "./corporateCarbon/CarbonAdminPage";
import { reportingRevisionStatusLabel as reportingRevisionLabel } from "./corporateCarbon/workflow";
export const CarbonBoundaryPage = ({
  accessToken,
  initialRevision = null,
  initialDecisions = [],
}) => {
  const contextToken = useTokenOps().account?.accessToken,
    token = accessToken || contextToken,
    generation = useRef(0),
    pendingRef = useRef(false);
  const [inventories, setInventories] = useState([]),
    [revisions, setRevisions] = useState(
      initialRevision ? [initialRevision] : [],
    ),
    [sites, setSites] = useState([]),
    [relationships, setRelationships] = useState([]),
    [inventoryId, setInventoryId] = useState(""),
    [revision, setRevision] = useState(initialRevision),
    [decisions, setDecisions] = useState(initialDecisions),
    [pending, setPending] = useState(false),
    [loading, setLoading] = useState(!initialRevision),
    [failed, setFailed] = useState(false),
    [error, setError] = useState(""),
    [reason, setReason] = useState(""),
    [repairPreview, setRepairPreview] = useState(null),
    [reload, setReload] = useState(0);
  const current = (g) => g === generation.current;
  useEffect(() => {
    const g = ++generation.current;
    pendingRef.current = false;
    setPending(false);
    setError("");
    setRepairPreview(null);
    setFailed(false);
    if (initialRevision) return;
    setInventoryId("");
    setRevision(null);
    setDecisions([]);
    setLoading(true);
    Promise.all([
      carbonRequest(token, "corporate-carbon/inventories"),
      carbonRequest(token, "corporate-carbon/sites?includeArchived=true"),
      carbonReferenceChoices(token, "controlRelationship"),
    ])
      .then(async ([i, s, relationshipChoices]) => {
        if (current(g)) {
          setInventories(i);
          setSites(s);
          setRelationships(relationshipChoices);
          const defaultInventory = i.find((item) => item.status === "active") || i[0];
          if (defaultInventory) {
            const revisionRows = await carbonRequest(token, `corporate-carbon/inventories/${defaultInventory.id}/revisions`);
            if (!current(g)) return;
            const defaultRevision = revisionRows[0];
            setInventoryId(defaultInventory.id);
            setRevisions(revisionRows);
            setRevision(defaultRevision || null);
            if (defaultRevision) {
              const boundaryRows = await carbonRequest(token, `corporate-carbon/revisions/${defaultRevision.id}/boundary-sites`);
              if (current(g)) setDecisions(boundaryRows);
            }
          }
        }
      })
      .catch((e) => {
        if (current(g)) {
          setError(e.message);
          setFailed(true);
        }
      })
      .finally(() => current(g) && setLoading(false));
    return () => {
      if (current(g)) generation.current = g + 1;
    };
  }, [token, reload, initialRevision]);
  const chooseInventory = async (id) => {
    pendingRef.current = false;
    setPending(false);
    setInventoryId(id);
    setRevision(null);
    setDecisions([]);
    setFailed(false);
    setError("");
    setRepairPreview(null);
    const g = ++generation.current;
    setLoading(true);
    try {
      const rows = await carbonRequest(
        token,
        `corporate-carbon/inventories/${id}/revisions`,
      );
      if (current(g)) setRevisions(rows);
    } catch (e) {
      if (current(g)) {
        setError(e.message);
        setFailed(true);
        setInventoryId("");
      }
    } finally {
      if (current(g)) setLoading(false);
    }
  };
  const chooseRevision = async (id) => {
    pendingRef.current = false;
    setPending(false);
    const selected = revisions.find((x) => x.id === id);
    setRevision(selected);
    setDecisions([]);
    setFailed(false);
    setError("");
    setRepairPreview(null);
    if (!selected) return;
    const g = ++generation.current;
    setLoading(true);
    try {
      const rows = await carbonRequest(
        token,
        `corporate-carbon/revisions/${id}/boundary-sites`,
      );
      if (current(g)) setDecisions(rows);
    } catch (e) {
      if (current(g)) {
        setError(e.message);
        setFailed(true);
      }
    } finally {
      if (current(g)) setLoading(false);
    }
  };
  const toggle = (siteId, value) =>
    setDecisions((rows) => {
      const old = rows.find((x) => x.siteId === siteId);
      return old
        ? rows.map((x) => (x.siteId === siteId ? { ...x, included: value } : x))
        : [
            ...rows,
            {
              siteId,
              included: value,
              warningState: "none",
              warningCode: null,
            },
          ];
    });
  const action = async (work) => {
    if (pendingRef.current) return;
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    try {
      await work();
      if (current(g) && revision) {
        const rows = await carbonRequest(
          token,
          `corporate-carbon/revisions/${revision.id}/boundary-sites`,
        );
        if (current(g)) setDecisions(rows);
      }
    } catch (e) {
      if (current(g)) {
        setError(e.message);
        setFailed(true);
      }
    } finally {
      pendingRef.current = false;
      if (current(g)) setPending(false);
    }
  };
  const save = () =>
    action(() =>
      carbonRequest(
        token,
        `corporate-carbon/revisions/${revision.id}/boundary-sites`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decisions: visibleSites
              .filter((site) => site.status !== "archived")
              .map((site) => {
                const decision = decisions.find((x) => x.siteId === site.id);
                return {
                  siteId: site.id,
                  included: decision?.included ?? false,
                };
              }),
          }),
        },
      ),
    );
  const acknowledge = (row) =>
    action(() =>
      carbonRequest(
        token,
        `corporate-carbon/boundary-sites/${row.id}/acknowledge`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        },
      ),
    );
  const previewInheritedSites = async () => {
    if (!revision || pendingRef.current) return;
    const g = generation.current;
    pendingRef.current = true;
    setPending(true);
    setRepairPreview(null);
    try {
      const value = await carbonRequest(token, `corporate-carbon/revisions/${revision.id}/boundary-repair`);
      if (current(g)) setRepairPreview(value);
    } catch (e) { if (current(g)) setError(e.message); }
    finally { pendingRef.current = false; if (current(g)) setPending(false); }
  };
  const restoreInheritedSites = () => {
    if (!repairPreview?.previewToken || !repairPreview.sites?.length) return;
    const previewToken = repairPreview.previewToken;
    return action(async () => {
      await carbonRequest(token, `corporate-carbon/revisions/${revision.id}/boundary-repair`, {
        method: "POST", body: JSON.stringify({ previewToken }),
      });
      setRepairPreview(null);
    });
  };
  const visibleSites = [
    ...sites.filter(
      (site) =>
        site.status !== "archived" ||
        decisions.some((decision) => decision.siteId === site.id),
    ),
    ...decisions
      .filter((decision) => !sites.some((site) => site.id === decision.siteId))
      .map((item) => ({ id: item.siteId, code: item.siteId })),
  ];
  const draft = revision?.status === "draft" && !failed;
  return (
    <CarbonAdminPage
      title="Inventory boundary"
      description="Define the organizational boundary and review inclusion decisions for a draft revision."
    >
        {loading && <div role="status">Loading boundaries…</div>}
        {error && <Typography role="alert">{error}</Typography>}
        {failed && (
          <Button
            onClick={() =>
              revision ? chooseRevision(revision.id) : setReload((v) => v + 1)
            }
          >
            {revision ? "Retry selected revision" : "Retry loading boundaries"}
          </Button>
        )}
        {!initialRevision && (
          <CarbonFilterBar label="Boundary filters">
            <CarbonSelectInput choices={inventories} choiceText={(item) => String(item.reportingYear)} label="Inventory" name="inventoryId" value={inventoryId} onChange={(event) => chooseInventory(event.target.value)} />
            <CarbonSelectInput choices={revisions} choiceText={reportingRevisionLabel} label="Revision" name="revisionId" value={revision?.id || ""} onChange={(event) => chooseRevision(event.target.value)} />
          </CarbonFilterBar>
        )}
        {!revision && (
          <Typography>Select a revision to edit its boundary.</Typography>
        )}
        {revision && visibleSites.length > 0 && (
          <CarbonSection title="Sites in this boundary" description="Choose which reporting locations belong to this inventory revision and acknowledge any required exceptions.">
            <CarbonDataTable label="Inventory boundary sites">
              <TableHead><TableRow><TableCell>Site</TableCell><TableCell>Control relationship at inclusion</TableCell><TableCell>Included</TableCell><TableCell>Warning</TableCell><TableCell>Acknowledgement</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>{visibleSites.map((site) => {
            const row = decisions.find((x) => x.siteId === site.id) || {
              siteId: site.id,
              included: false,
            };
            return (
              <TableRow key={site.id}>
                <TableCell><strong>{site.code || site.id}</strong>{site.name ? <><br />{site.name}</> : null}</TableCell>
                <TableCell>{relationships.find((item) => item.id === row.controlRelationshipId)?.name || row.controlRelationshipId || "—"}</TableCell>
                <TableCell>{draft ? <FormControlLabel control={<Checkbox checked={row.included} disabled={pending || site.status === "archived"} onChange={(event) => toggle(site.id, event.target.checked)} />} label={`Include site ${site.code || site.id}`} /> : (row.included ? "Included" : "Excluded")}</TableCell>
                <TableCell>{row.warningCode ? <Typography role="alert">Boundary acknowledgement required</Typography> : "—"}</TableCell>
                <TableCell>{row.acknowledgementReason ? <>Acknowledged by {row.acknowledgedByUserId}: {row.acknowledgementReason}</> : "—"}</TableCell>
                <TableCell align="right">{draft && row.warningState === "required" ? (
                  <CarbonTableActions label={`Actions for ${site.code || site.id}`}>
                    <CarbonTextInput label={`Acknowledgement reason for ${site.code || site.id}`} name={`acknowledgement-${site.id}`} value={reason} onChange={(event) => setReason(event.target.value)} />
                    <Button color="primary" disabled={pending || !reason.trim()} onClick={() => acknowledge(row)}>Acknowledge warning</Button>
                  </CarbonTableActions>
                ) : null}</TableCell>
              </TableRow>
            );
          })}</TableBody>
            </CarbonDataTable>
          </CarbonSection>
        )}
        {revision && draft && (
          <>
            <CarbonSection title="Inherited boundary repair" description="Preview sites missing from this draft that were included in its predecessor. Existing boundary decisions are preserved.">
              <Button disabled={pending} onClick={previewInheritedSites} variant="outlined">Preview inherited sites</Button>
              {repairPreview && (repairPreview.sites || []).length === 0 && <Typography>No inherited sites need restoring.</Typography>}
              {repairPreview && (repairPreview.sites || []).length > 0 && <>
                <Typography>{repairPreview.preservedCount} existing boundary decisions remain unchanged.</Typography>
                <ul>{repairPreview.sites.map((site) => <li key={site.siteId}>{site.name || site.code} · {site.controlRelationship} · {site.included ? "included" : "excluded"}</li>)}</ul>
                <Button color="primary" disabled={pending} onClick={restoreInheritedSites} variant="outlined">Restore missing sites</Button>
              </>}
            </CarbonSection>
            <Button color="primary" disabled={pending} onClick={save} variant="contained">Save boundary</Button>
          </>
        )}
    </CarbonAdminPage>
  );
};
const resource = () => (
  <Resource
    name="carbonBoundaries"
    options={{ label: "Boundaries", menuGroup: "Carbon Accounting" }}
    list={CarbonBoundaryPage}
  />
);
export default resource;
